import { ReactNode, useState } from 'react';
import * as R from 'remeda';
import { IconCustomMenuDown, IconCustomMenuUp } from '@rossum/ui/icons/tabler';
import {
  Checkbox,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Radio,
  Skeleton,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
} from '@rossum/ui/material';
import { highlightSearchedValue } from '../highlightSearchedValue';
import { OptionGroup } from '../types';

export const SelectOptionsSkeleton = () => {
  return (
    <Stack spacing={0.3} width="100%">
      {[1, 2, 3, 4, 5].map(i => (
        <Stack key={i} flexDirection="row" alignItems="center" gap={2}>
          <Checkbox disabled sx={{ p: 0 }} />
          <Skeleton height={30} width="100%" variant="text" />
        </Stack>
      ))}
    </Stack>
  );
};

type SelectOptionsListProps = {
  children: ReactNode;
  fullHeight?: boolean;
};

export const SelectOptionsList = ({
  children,
  fullHeight,
}: SelectOptionsListProps) => {
  return (
    <List
      disablePadding
      sx={{
        width: '100%',
        maxHeight: fullHeight ? '100%' : 'max(300px,40vh)',
        overflow: 'auto',
      }}
    >
      {children}
    </List>
  );
};

type SelectOptionProps = {
  checked: boolean;
  label: ReactNode;
  handleOnClick: () => void;
  isSingleSelect?: boolean;
  dataCy?: string;
};

export const SelectOption = ({
  handleOnClick,
  label,
  checked,
  isSingleSelect,
  dataCy,
}: SelectOptionProps) => {
  const SelectComponent = isSingleSelect ? Radio : Checkbox;

  return (
    <ListItem disablePadding disableGutters dense data-cy={dataCy}>
      <ListItemButton disableRipple onClick={handleOnClick}>
        <ListItemIcon sx={{ minWidth: 0, mr: 2 }}>
          <SelectComponent checked={checked} sx={{ p: 0 }} />
        </ListItemIcon>
        <ListItemText sx={{ m: 0 }}>
          <Tooltip title={label} enterDelay={1000} enterNextDelay={500}>
            <Typography
              variant="body2"
              sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {label}
            </Typography>
          </Tooltip>
        </ListItemText>
      </ListItemButton>
    </ListItem>
  );
};

type GroupedOptionsListProps = {
  groupedOptions: OptionGroup[];
  selectedValues: Record<string, boolean>;
  onClickOnAll?: (options: Record<string, boolean>) => void;
  renderOptions: (options: string[]) => ReactNode;
  searchValue: string;
};

export const GroupedOptionsList = ({
  groupedOptions,
  selectedValues,
  onClickOnAll,
  renderOptions,
  searchValue,
}: GroupedOptionsListProps) => {
  const [expandedGroups, setExpandedGroups] = useState<string[]>(() =>
    groupedOptions
      .filter(g => g.options.some(o => selectedValues[o]))
      .map(g => g.id)
  );

  return (
    <List
      sx={{
        width: '100%',
        maxHeight: 'max(300px,40vh)',
        overflow: 'auto',
      }}
    >
      {groupedOptions.map(({ id, label, options }) => {
        const isChecked = options.every(o => selectedValues[o]);

        const isIndeterminate =
          !isChecked && options.some(o => selectedValues[o]);

        return (
          <Stack key={id}>
            <ListItemButton
              dense
              onClick={() =>
                setExpandedGroups(prev =>
                  prev.includes(id)
                    ? prev.filter(prevId => prevId !== id)
                    : [...prev, id]
                )
              }
            >
              {onClickOnAll ? (
                <ListItemIcon
                  sx={{ minWidth: 0, mr: 1 }}
                  onClick={e => e.stopPropagation()}
                >
                  <Checkbox
                    indeterminate={isIndeterminate}
                    checked={isChecked}
                    onChange={(_, value) => {
                      const selected = R.mapToObj(options, op => [op, value]);
                      onClickOnAll(selected);
                    }}
                    sx={{ p: 0 }}
                  />
                </ListItemIcon>
              ) : null}
              <Typography
                mr="auto"
                variant="body2"
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {searchValue
                  ? highlightSearchedValue(label, searchValue)
                  : label}
              </Typography>
              <SvgIcon fontSize="large">
                {expandedGroups.includes(id) ? (
                  <IconCustomMenuUp />
                ) : (
                  <IconCustomMenuDown />
                )}
              </SvgIcon>
            </ListItemButton>
            <Collapse in={!!searchValue || expandedGroups.includes(id)}>
              <Stack sx={{ pl: 1 }}>
                <SelectOptionsList fullHeight>
                  {renderOptions(options)}
                </SelectOptionsList>
              </Stack>
            </Collapse>
          </Stack>
        );
      })}
    </List>
  );
};
